import { Dialog } from '@headlessui/react'
import { motion } from 'framer-motion'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React, { useMemo, useState } from 'react'

import ImageByWidthDevice from '@/components/ImageByWidthDevice'
import { introLeftVariants, introOpacityVariants, introRightVariants } from '@/config/const'
import { getHomeAbout, getHomeGlimpse } from '@/services/home'
import { BasicHeaders } from '@/types/common'
import { useAppSelector } from '@/utils/hooks'

import OpeningBackground from '~/assets/images/opening-bg.webp'

const Opening: React.FC = () => {
  const router = useRouter()
  const { t } = useTranslation()
  const [openDialog, setOpenDialog] = useState(false)

  const requestList: BasicHeaders = useMemo(() => {
    return {
      headers: { 'Accept-Language': String(router.locale) },
    }
  }, [])

  const { data: about } = useAppSelector(getHomeAbout.select(requestList))

  const handleCloseAndOpen = () => {
    setOpenDialog((state) => !state)
  }

  const request: BasicHeaders = useMemo(() => {
    return {
      headers: { 'Accept-Language': String(router.locale) },
    }
  }, [])

  const { data: distributionMap } = useAppSelector(getHomeGlimpse.select(request))

  return (
    <section className='relative overflow-hidden bg-white'>
      <div>
        <motion.div
          className='absolute left-0 z-[0] hidden h-[783px] w-[436px] lg:block'
          initial='hide'
          whileInView='show'
          viewport={{ once: true }}
          variants={introOpacityVariants}
        >
          <img
            src={OpeningBackground.src}
            alt='opening background'
            className='absolute left-0 z-[0] hidden h-[783px] w-[436px] lg:block'
          />
        </motion.div>
        <div
          id='opening'
          className='relative z-[1] flex h-full w-full items-center justify-center'
        >
          <div className='container z-10 flex w-full flex-col-reverse justify-center pt-8 lg:flex-row lg:pt-12.5'>
            <motion.div
              className='flex flex-1 flex-col space-y-6 pt-0 lg:gap-x-8.5 lg:space-y-8.5 lg:pt-[98px]'
              initial='hide'
              whileInView='show'
              viewport={{ once: true }}
              variants={introLeftVariants}
            >
              <div className='hidden flex-col gap-1 lg:flex'>
                <p className='font-bold leading-normal text-red'>{about?.data.subTitle}</p>
                <h2 className='text-[32px] font-bold leading-normal'>{about?.data.title}</h2>
              </div>

              <div
                className='description break-words text-center text-sm text-gray-1 lg:text-left lg:text-base'
                dangerouslySetInnerHTML={{ __html: String(about?.data.description) }}
              />

              <div className='flex gap-4'>
                <Link
                  href={about?.data.button.learnMore.url ?? ''}
                  className='flex w-full items-center justify-center gap-2 rounded-[100px] bg-red px-5 py-3 text-base text-sm font-bold text-white lg:w-fit lg:px-6'
                >
                  <span>{about?.data.button.learnMore.title}</span>
                  <span className='kek-arrow-right text-2xl' />
                </Link>
                <button
                  className='flex w-full items-center justify-center gap-2 rounded-[100px] bg-[#16A1A1] px-5 py-3 text-base text-sm font-bold text-white lg:w-fit lg:px-6'
                  onClick={handleCloseAndOpen}
                >
                  <span>{about?.data.button.video.title}</span>
                  <span className='kek-play text-2xl' />
                </button>
              </div>
            </motion.div>

            <motion.div
              className='lg:basis-[531px]'
              initial='hide'
              whileInView='show'
              viewport={{ once: true }}
              variants={introRightVariants}
            >
              <div className='flex flex-col gap-6'>
                <div className='flex flex-col gap-2 lg:hidden'>
                  <p className='text-center text-xs font-semibold leading-normal text-red'>{about?.data.subTitle}</p>
                  <h2 className='text-center text-[24px] font-bold leading-normal lg:text-[32px]'>
                    {about?.data.title}
                  </h2>
                </div>

                <div className='relative w-full'>
                  {about?.data?.imageFiles && (
                    <ImageByWidthDevice
                      images={about.data.imageFiles}
                      alt='opening image'
                      className='w-full'
                    />
                  )}
                  <div className='absolute inset-0' />
                </div>
              </div>
            </motion.div>
          </div>
          <Dialog
            open={openDialog}
            onClose={handleCloseAndOpen}
            className='fixed inset-0 z-menu flex h-full w-full items-center justify-center bg-black/80 '
          >
            <div className='container flex flex-col items-center justify-center gap-6'>
              <div className='flex w-full items-center justify-end'>
                <button
                  onClick={handleCloseAndOpen}
                  className='rounded-full bg-red px-5 py-3 font-bold text-white'
                >
                  {t('common:close')}
                </button>
              </div>

              <div className='relative h-[229px] w-full md:h-[400px] lg:h-[517px]'>
                <div className='w-full'>
                  <iframe
                    src={about?.data.button.video.url ?? ''}
                    allow='encrypted-media;'
                    className='h-[193px] w-full rounded-lg md:h-[468px]'
                  />
                </div>
              </div>
            </div>
          </Dialog>
        </div>
      </div>

      <div className='flex h-auto justify-center pt-8 lg:h-[147px]'>
        <div className='flex w-[1110px] flex-col items-center'>
          <motion.div
            className='mb-6 flex w-auto gap-2.5 lg:mb-4'
            initial='hide'
            whileInView='show'
            viewport={{ once: true }}
            variants={introLeftVariants}
          >
            <div className='text-2xl font-semibold leading-10 text-neutral-700 lg:text-[34px]'>
              {t('home:distributionMaps.title')}
            </div>{' '}
            <div className='relative text-2xl font-extrabold uppercase leading-10 text-red lg:text-[34px]'>
              Indonesia
              <div className='absolute bottom-0 left-1 w-[120px] rotate-[1deg] rounded-lg border border-orange lg:w-[167px]' />
              <div className='absolute -bottom-[1.5px] left-3  w-[123px]  rounded-lg border border-green lg:w-[169px]' />
            </div>
          </motion.div>

          <motion.div
            className='description text-dark-2 mx-4 break-words text-center text-sm md:mx-0 lg:text-base'
            dangerouslySetInnerHTML={{ __html: String(distributionMap?.data.description) }}
            initial='hide'
            whileInView='show'
            viewport={{ once: true }}
            variants={introOpacityVariants}
          />
        </div>
      </div>
    </section>
  )
}

export default Opening
